import { useId, useRef, useState, type KeyboardEvent } from "react";
import { fehlertext } from "../../kern/fehler";
import { Feld } from "../../kern/ui/Feld";
import { Knopf } from "../../kern/ui/Knopf";
import { useHinweis } from "../../kern/ui/Hinweise";
import ui from "../../kern/ui/ui.module.css";
import { useKatalogAnlegen } from "./daten";
import { KATALOG, KATALOG_NEU, katalogName, type KatalogArt } from "./logik";
import s from "./aufgaben.module.css";

interface Props {
  art: KatalogArt;
  wert: string | null;
  eintraege: readonly { id: string; name: string }[] | undefined;
  /** Ressort: Recht sprint.planen, Vorhaben: Recht vorhaben.verwalten. Nur dann gibt es den Eintrag zum Anlegen */
  darfAnlegen: boolean;
  gesperrt?: boolean;
  /** Sichtbare Beschriftung (Neue Aufgabe). Ohne sie nur für Screenreader (Aufgabe, dort steht die Beschriftung in der Zeile). */
  labelSichtbar?: boolean;
  /** Ressort, zu dem ein neues Vorhaben gehört (das Ressort der Aufgabe) */
  ressortFuerVorhaben?: string | null;
  onWechsel: (id: string | null) => void;
}

/** Auswahl für Ressort oder Vorhaben, mit „Neues Ressort …“ bzw. „Neues Vorhaben …“ für alle mit dem Recht dazu.
 *  Das Eingabefeld ist kein eigenes Formular, weil „Neue Aufgabe“ selbst eins ist: Enter legt an, Escape bricht ab. */
export function KatalogAuswahl({ art, wert, eintraege, darfAnlegen, gesperrt, labelSichtbar, ressortFuerVorhaben, onWechsel }: Props) {
  const k = KATALOG[art];
  const id = useId();
  const auswahl = useRef<HTMLSelectElement>(null);
  const { zeigen } = useHinweis();
  const anlegen = useKatalogAnlegen();
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);

  function schliessen() {
    setOffen(false);
    setName("");
    setFehler(null);
    auswahl.current?.focus();
  }

  async function absenden() {
    const n = katalogName(art, name);
    if (!n.ok) return setFehler(n.fehler);
    setFehler(null);
    try {
      const neu = await anlegen.mutateAsync({ art, name: n.name, ressort: art === "vorhaben" ? (ressortFuerVorhaben ?? null) : null });
      onWechsel(neu);
      zeigen(`${k.einzahl} „${n.name}“ angelegt.`);
      schliessen();
    } catch (err) {
      setFehler(fehlertext(err));
    }
  }

  function taste(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!anlegen.isPending) void absenden();
    } else if (e.key === "Escape") {
      e.preventDefault();
      schliessen();
    }
  }

  return (
    <div className={[labelSichtbar ? ui.feld : null, s.katalog].filter(Boolean).join(" ")}>
      <label htmlFor={id} className={labelSichtbar ? ui.label : "nur-leser"}>
        {k.einzahl}
      </label>
      <select
        id={id}
        ref={auswahl}
        className={s.auswahl}
        value={wert ?? ""}
        disabled={gesperrt || anlegen.isPending}
        onChange={(e) => {
          if (e.target.value === KATALOG_NEU) setOffen(true);
          else onWechsel(e.target.value || null);
        }}
      >
        <option value="">{k.ohne}</option>
        {eintraege?.map((x) => (
          <option key={x.id} value={x.id}>
            {x.name}
          </option>
        ))}
        {darfAnlegen && !gesperrt ? <option value={KATALOG_NEU}>{k.neu}</option> : null}
      </select>
      {offen ? (
        <div className={[ui.innen, s.katalogNeu].join(" ")}>
          <Feld label={k.nameLabel} value={name} maxLength={k.max} autoFocus fehler={fehler} onChange={(e) => setName(e.target.value)} onKeyDown={taste} />
          <div className={s.katalogKnoepfe}>
            <Knopf art="primaer" ohnePunkt klein onClick={() => void absenden()} disabled={anlegen.isPending || !name.trim()}>
              {anlegen.isPending ? "Einen Moment …" : `${k.einzahl} anlegen`}
            </Knopf>
            <Knopf art="leise" klein onClick={schliessen} disabled={anlegen.isPending}>
              Abbrechen
            </Knopf>
          </div>
        </div>
      ) : null}
    </div>
  );
}
